<template>
  <div class="embedded-editor-container">
    <!-- Toolbar Compacta -->
    <div
      v-if="editable"
      class="compact-toolbar"
    >
      <div class="toolbar-row">
        <!-- Formatação Básica -->
        <div class="toolbar-group compact-group">
          <button
            :class="{ 'toolbar-btn--active': editor?.isActive('bold') }"
            class="toolbar-btn compact-btn"
            title="Negrito"
            @click="editor.chain().focus().toggleBold().run()"
          >
            <span class="btn-icon">B</span>
          </button>
          <button
            :class="{ 'toolbar-btn--active': editor?.isActive('italic') }"
            class="toolbar-btn compact-btn"
            title="Itálico"
            @click="editor.chain().focus().toggleItalic().run()"
          >
            <span class="btn-icon">I</span>
          </button>
          <button
            :class="{ 'toolbar-btn--active': editor?.isActive('underline') }"
            class="toolbar-btn compact-btn"
            title="Sublinhado"
            @click="editor.chain().focus().toggleUnderline().run()"
          >
            <span class="btn-icon">U</span>
          </button>
          <button
            :class="{ 'toolbar-btn--active': editor?.isActive('strike') }"
            class="toolbar-btn compact-btn"
            title="Tachado"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <span class="btn-icon">S</span>
          </button>
        </div>

        <!-- Cabeçalhos -->
        <div class="toolbar-group compact-group">
          <select 
            v-model="currentHeading" 
            class="heading-select compact-btn"
            title="Estilo de texto"
            @change="setHeading($event)"
          >
            <option value="paragraph">
              Texto
            </option>
            <option value="heading1">
              Título 1
            </option>
            <option value="heading2">
              Título 2
            </option>
            <option value="heading3">
              Título 3
            </option>
          </select>
        </div>

        <!-- Listas -->
        <div class="toolbar-group compact-group">
          <button
            :class="{ 'toolbar-btn--active': editor?.isActive('bulletList') }"
            class="toolbar-btn compact-btn"
            title="Lista com marcadores"
            @click="editor.chain().focus().toggleBulletList().run()"
          >
            <span class="btn-icon">•</span>
          </button>
          <button
            :class="{ 'toolbar-btn--active': editor?.isActive('orderedList') }"
            class="toolbar-btn compact-btn"
            title="Lista numerada"
            @click="editor.chain().focus().toggleOrderedList().run()"
          >
            <span class="btn-icon">1.</span>
          </button>
        </div>

        <!-- Alinhamento -->
        <div class="toolbar-group compact-group">
          <button
            :class="{ 'toolbar-btn--active': currentAlignment === 'left' }"
            class="toolbar-btn compact-btn"
            title="Alinhar à esquerda"
            @click="setAlignment('left')"
          >
            <span class="btn-icon">⫷</span>
          </button>
          <button
            :class="{ 'toolbar-btn--active': currentAlignment === 'center' }"
            class="toolbar-btn compact-btn"
            title="Centralizar"
            @click="setAlignment('center')"
          >
            <span class="btn-icon">⫸</span>
          </button>
          <button
            :class="{ 'toolbar-btn--active': currentAlignment === 'right' }"
            class="toolbar-btn compact-btn"
            title="Alinhar à direita"
            @click="setAlignment('right')"
          >
            <span class="btn-icon">⫹</span>
          </button>
        </div>

        <!-- Elementos Especiais -->
        <div class="toolbar-group compact-group">
          <button
            :class="{ 'toolbar-btn--active': editor?.isActive('blockquote') }"
            class="toolbar-btn compact-btn"
            title="Citação"
            @click="editor.chain().focus().toggleBlockquote().run()"
          >
            <span class="btn-icon">"</span>
          </button>
          <button
            class="toolbar-btn compact-btn"
            title="Linha horizontal"
            @click="setHorizontalRule"
          >
            <span class="btn-icon">―</span>
          </button>
        </div>

        <!-- Ações -->
        <div class="toolbar-group compact-group">
          <button
            :disabled="!editor?.can().undo()"
            class="toolbar-btn compact-btn action-btn"
            title="Desfazer"
            @click="editor.chain().focus().undo().run()"
          >
            <span class="btn-icon">↶</span>
          </button>
          <button
            :disabled="!editor?.can().redo()"
            class="toolbar-btn compact-btn action-btn"
            title="Refazer"
            @click="editor.chain().focus().redo().run()"
          >
            <span class="btn-icon">↷</span>
          </button>
          <button
            class="toolbar-btn compact-btn action-btn"
            title="Limpar"
            @click="clearContent"
          >
            <span class="btn-icon">×</span>
          </button>
        </div>

        <!-- Status e Contador -->
        <div class="toolbar-status">
          <div class="character-count compact-count">
            {{ characterCount }}
          </div>
          <div class="editor-status compact-status">
            <v-icon
              x-small
              class="status-icon"
              :color="editor?.isFocused ? 'success' : 'grey'"
            >
              {{ editor?.isFocused ? 'circle' : 'radio_button_unchecked' }}
            </v-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- Área de Conteúdo -->
    <div
      class="editor-content-wrapper"
      :class="{ 'readonly-mode': !editable }"
      @click="focusEditor"
    >
      <editor-content
        :editor="editor"
        class="compact-editor-content"
      />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'

export default {
  name: 'CompactTextEditor',
  
  components: {
    EditorContent,
  },
  
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Digite seu texto aqui...'
    },
    editable: {
      type: Boolean,
      default: true
    },
    minHeight: {
      type: String,
      default: '400px'
    }
  },
  
  emits: [
    'update:modelValue',
    'update',
    'blur',
    'focus',
    'change',
    'character-count-change'
  ],
  
  data() {
    return {
      editor: null,
      currentAlignment: 'left',
      currentHeading: 'paragraph',
      characterCount: 0
    }
  },
  
  watch: {
    modelValue(value) {
      if (this.editor && value !== this.editor.getHTML()) {
        this.editor.commands.setContent(value, false)
        this.updateCharacterCount()
      }
    },
    editable(value) {
      if (this.editor) {
        this.editor.setEditable(value)
      }
    }
  },
  
  mounted() {
    this.editor = new Editor({
      content: this.modelValue,
      editable: this.editable,
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3],
          },
        }),
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
          alignments: ['left', 'center', 'right', 'justify'],
          defaultAlignment: 'left',
        }),
      ],
      onUpdate: () => {
        const html = this.editor.getHTML()
        this.$emit('update:modelValue', html)
        this.$emit('update', html)
        this.updateCharacterCount()
        this.updateCurrentAlignment()
        this.updateCurrentHeading()
      },
      onBlur: () => {
        this.$emit('blur')
      },
      onFocus: () => {
        this.$emit('focus')
      },
      onTransaction: () => {
        this.updateCurrentAlignment()
        this.updateCurrentHeading()
      }
    })

    this.updateCharacterCount()
  },
  
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy()
    }
  },
  
  methods: {
    setAlignment(alignment) {
      if (this.editor) {
        this.editor.chain().focus().setTextAlign(alignment).run()
        this.currentAlignment = alignment
      }
    },
    
    setHeading(event) {
      if (!this.editor) return
      
      const value = event.target.value
      
      switch (value) {
        case 'paragraph':
          this.editor.chain().focus().setParagraph().run()
          break
        case 'heading1':
          this.editor.chain().focus().setHeading({ level: 1 }).run()
          break
        case 'heading2':
          this.editor.chain().focus().setHeading({ level: 2 }).run()
          break
        case 'heading3':
          this.editor.chain().focus().setHeading({ level: 3 }).run()
          break
      }
    },
    
    setHorizontalRule() {
      if (this.editor) {
        this.editor.chain().focus().setHorizontalRule().run()
      }
    },
    
    updateCurrentAlignment() {
      if (!this.editor) return
      
      if (this.editor.isActive({ textAlign: 'left' })) {
        this.currentAlignment = 'left'
      } else if (this.editor.isActive({ textAlign: 'center' })) {
        this.currentAlignment = 'center'
      } else if (this.editor.isActive({ textAlign: 'right' })) {
        this.currentAlignment = 'right'
      } else if (this.editor.isActive({ textAlign: 'justify' })) {
        this.currentAlignment = 'justify'
      }
    },
    
    updateCurrentHeading() {
      if (!this.editor) return
      
      if (this.editor.isActive('heading', { level: 1 })) {
        this.currentHeading = 'heading1'
      } else if (this.editor.isActive('heading', { level: 2 })) {
        this.currentHeading = 'heading2'
      } else if (this.editor.isActive('heading', { level: 3 })) {
        this.currentHeading = 'heading3'
      } else {
        this.currentHeading = 'paragraph'
      }
    },
    
    updateCharacterCount() {
      if (this.editor) {
        this.characterCount = this.editor.getText().length
        this.$emit('character-count-change', this.characterCount)
      }
    },
    
    focusEditor() {
      if (this.editor && !this.editor.isFocused) {
        this.editor.chain().focus().run()
      }
    },

    clearContent() {
      if (this.editor) {
        this.editor.commands.clearContent()
        this.updateCharacterCount()
      }
    }
  }
}
</script>

<style scoped>
.embedded-editor-container {
  display: flex;
  flex-direction: column;
  background-color: white !important; /* Fundo branco para o container principal */
  border: 1px solid rgba(0, 0, 0, 0.12); /* Borda escura suave */
  border-radius: 6px;
  overflow: hidden;
  min-height: 400px;
}

.editor-content-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: white;
  color: #212121;
}

.compact-editor-content {
  padding: 10px 12px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  flex: 1;
  overflow-y: auto;
}

:deep(.ProseMirror) {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  word-wrap: break-word;
  overflow-wrap: break-word;
  color: #212121 !important; /* Cor de texto principal escura */
  outline: none !important;
}

.compact-toolbar {
  padding: 4px 5px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12); /* Borda escura suave */
  background-color: #f5f5f5 !important; /* Fundo da toolbar ligeiramente cinza */
}

.toolbar-row {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: nowrap;
}

.compact-group {
  display: flex;
  gap: 2px;
  background-color: transparent !important;
  padding: 3px;
  border-radius: 5px;
}

.compact-btn {
  padding: 3px 5px;
  border: 1px solid #ccc !important; /* Borda visível */
  background-color: #fff !important; /* Fundo branco */
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 600;
  min-width: 24px;
  height: 22px;
  transition: all 0.15s ease;
  color: #333 !important; /* Cor de texto escura */
  display: flex;
  align-items: center;
  justify-content: center;
}

.compact-btn:hover {
  background: #e0e0e0 !important; /* Cinza claro no hover */
  border-color: #bbb !important;
  color: #212121 !important;
}

.toolbar-btn--active {
  background: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
  border-color: rgb(var(--v-theme-primary)) !important;
}

.compact-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f5f5f5 !important;
  border-color: #ddd !important;
  color: #999 !important;
}

.compact-btn:disabled:hover {
  background: #f5f5f5 !important;
  border-color: #ddd !important;
  color: #999 !important;
}

.btn-icon {
  font-weight: 700;
  font-size: 0.75rem;
}

.heading-select {
  font-size: 0.7rem;
  padding: 0 6px;
  cursor: pointer;
  outline: none;
  min-width: 70px;
  background-color: #fff !important;
  color: #333 !important;
  border: 1px solid #ccc !important;
  border-radius: 4px;
  height: 22px;
}

.heading-select option {
  font-size: 0.7rem;
  padding: 4px;
  color: #333;
  background-color: #fff;
}

.toolbar-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  padding: 0 4px;
}

.compact-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #555 !important;
  background-color: white !important;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  min-width: 45px;
  text-align: center;
}

.compact-status {
  display: flex;
  align-items: center;
}

.status-icon {
  font-size: 14px !important;
  color: #555;
}

.editor-content-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.readonly-mode {
  background-color: #fafafa !important; /* Fundo ligeiramente cinza para modo leitura */
}

:deep(.ProseMirror p),
:deep(.ProseMirror ul li),
:deep(.ProseMirror ol li),
:deep(.ProseMirror strong),
:deep(.ProseMirror em),
:deep(.ProseMirror u),
:deep(.ProseMirror s) {
  color: #212121 !important;
}

:deep(.ProseMirror h1),
:deep(.ProseMirror h2),
:deep(.ProseMirror h3) {
  color: #1a1a1a !important;
  font-weight: 700;
}

:deep(.ProseMirror h1) { font-size: 1.3rem; margin: 0.5em 0 0.3em 0; }
:deep(.ProseMirror h2) { font-size: 1.15rem; margin: 0.4em 0 0.2em 0; }
:deep(.ProseMirror h3) { font-size: 1.05rem; margin: 0.3em 0 0.2em 0; }

:deep(.ProseMirror ul),
:deep(.ProseMirror ol) {
  padding-left: 1.2em;
  margin: 0.5em 0;
}

:deep(.ProseMirror blockquote) {
  border-left: 3px solid #ccc;
  padding: 0.5em 0.8em;
  margin: 0.5em 0;
  font-style: italic;
  background-color: #f9f9f9 !important;
  border-radius: 0 4px 4px 0;
  color: #555 !important;
}

:deep(.ProseMirror hr) {
  border: none;
  border-top: 2px solid rgba(0, 0, 0, 0.1) !important;
  margin: 1em 0;
}

:deep(.ProseMirror .is-empty:before) {
  color: #aaa !important; /* Cinza para placeholder */
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
  font-style: italic;
  font-size: 0.9rem;
}

.compact-editor-content::-webkit-scrollbar,
:deep(.ProseMirror)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.compact-editor-content::-webkit-scrollbar-track,
:deep(.ProseMirror)::-webkit-scrollbar-track {
  background: transparent;
}

.compact-editor-content::-webkit-scrollbar-thumb,
:deep(.ProseMirror)::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.compact-editor-content::-webkit-scrollbar-thumb:hover,
:deep(.ProseMirror)::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.4);
}
</style>